<template>
  <aside class="app-sidebar" :class="{ collapsed }">
    <button class="brand" type="button" title="返回首页" @click="emit('navigate', '/')">
      <!-- 品牌图标用 2.0 原图（用户 2026-10-14 定）；尺寸按原型 .bu-logo：29、圆角 10、紫色柔阴影 -->
      <img src="/xbios-logo.jpg" alt="BioIntelOS" class="brand-logo" />
      <span class="brand-name">BioIntelOS</span>
      <span class="brand-version">v3.0</span>
    </button>

    <button class="sidebar-toggle" type="button" :title="collapsed ? '展开侧边栏' : '收起侧边栏'"
      :aria-label="collapsed ? '展开侧边栏' : '收起侧边栏'" @click="emit('toggle-collapse')">
      <!-- 原型 .bu-navigation-handle：栏缘外 32×48 点击区，内画 16×32 半圆，箭头 10px。 -->
      <span class="sidebar-toggle-face" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path :d="collapsed ? 'm9 6 6 6-6 6' : 'm15 6-6 6 6 6'"
            stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
    </button>

    <!-- 原型：未保存过研究偏好时蓝色高亮作引导；保存后（is_completed）恢复普通导航样式。 -->
    <button type="button" class="research-preference-entry" :class="{ 'is-preference-complete': preferenceComplete }"
      :title="collapsed ? '我的研究偏好' : undefined" @click="showResearchPreferenceDialog = true">
      <span class="preference-entry-icon" v-html="icons.preference"></span>
      <span class="preference-entry-label">我的研究偏好</span>
    </button>

    <div class="sidebar-section-label">核心入口</div>

    <nav class="sidebar-nav" aria-label="全局导航">
      <!-- 原型副本3：「开始研究」（原「我的项目」）排在能力空间前；首页是项目欢迎页，快速开始进对话页。 -->
      <button type="button" class="nav-item" :data-nav-path="researchEntry.path" :title="collapsed ? researchEntry.label : undefined"
        :class="{ active: isActive(researchEntry.path) }" @click="emit('navigate', researchEntry.path)">
        <span class="nav-icon" v-html="researchEntry.icon"></span>
        <span class="nav-label">{{ researchEntry.label }}</span>
      </button>

      <div class="capability-group">
        <button type="button" class="nav-item capability-trigger"
          :class="{ active: isCapabilityActive }" :title="collapsed ? '能力空间' : undefined"
          @click="openCapabilitySpace">
          <span class="nav-icon" v-html="icons.inspiration"></span>
          <span class="nav-label">能力空间</span>
        </button>
      </div>

      <button v-for="item in nav" :key="item.path" type="button" class="nav-item" :data-nav-path="item.path" :title="collapsed ? item.label : undefined"
        :class="{ active: isActive(item.path) }" @click="emit('navigate', item.path)">
        <span class="nav-icon" v-html="item.icon"></span>
        <span class="nav-label">{{ item.label }}</span>
      </button>
    </nav>

    <template v-if="workbenchItems.length">
      <div class="sidebar-section-label">工作台</div>

      <nav class="sidebar-nav" aria-label="工作台">
        <button v-for="item in workbenchItems" :key="item.path" type="button" class="nav-item"
          :title="collapsed ? item.label : undefined" :class="{ active: isActive(item.path) }"
          @click="emit('navigate', item.path)">
          <span class="nav-icon" v-html="item.icon"></span>
          <span class="nav-label">{{ item.label }}</span>
        </button>
      </nav>
    </template>

    <div class="sidebar-spacer"></div>

    <!-- 「免费领」用真实文字（原为 SVG 路径，读屏/搜索/复制都识别不到），视觉沿用原渐变胶囊 -->
    <button class="free-claim-btn" type="button" aria-label="免费领碱基币" title="免费领碱基币" @click="openPromotionDialog">
      <span class="free-claim-pill">免费领</span>
    </button>

    <button type="button" class="help-collapse-bar" :class="{ collapsed: helpCollapsed }"
      :aria-expanded="!helpCollapsed" :aria-label="helpCollapsed ? '展开帮助与支持' : '收起帮助与支持'"
      :title="helpCollapsed ? '展开帮助与支持' : '收起帮助与支持'" @click="helpCollapsed = !helpCollapsed">
      <span class="help-collapse-line"></span>
      <span class="help-collapse-chevron">
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M4.5 10 8 6.5 11.5 10" stroke="currentColor" stroke-width="1.5"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
      <span class="help-collapse-line"></span>
    </button>

    <nav v-show="!helpCollapsed" class="sidebar-tools" aria-label="帮助与支持">
      <button v-for="item in helpItems" :key="item.command" type="button" class="nav-item tool-item"
        :class="{ 'message-nav-item': item.command === 'messages' }"
        :aria-label="item.command === 'messages' && messageUnreadCount ? `消息通知，${messageUnreadCount} 条未读消息` : item.label"
        :title="collapsed ? item.label : undefined" @click="handleHelpCommand(item.command)">
        <span class="nav-icon" v-html="item.icon"></span>
        <span class="nav-label">{{ item.label }}</span>
        <span v-if="item.command === 'messages' && messageUnreadCount > 0" class="message-nav-badge" aria-hidden="true">{{ messageUnreadCount > 99 ? '99+' : messageUnreadCount }}</span>
      </button>
    </nav>

    <button class="nav-item coin-item" :class="{ active: isActive('/billing') }" type="button"
      :title="collapsed ? `碱基币 ${formatBalance(balance)}` : undefined" @click="emit('navigate', '/billing')">
      <span class="coin-icon" v-html="icons.coin"></span>
      <span class="nav-label">碱基币</span>
      <span class="balance-amount">{{ formatBalance(balance) }}</span>
      <span v-if="!messagesEnabled && unreadGiftCount" class="coin-reminder" :title="`${unreadGiftCount} 份会员赠送待提醒`">{{ unreadGiftCount }}</span>
    </button>

    <el-dropdown v-if="user || hasToken" trigger="click" placement="right-end" class="help-dropdown" popper-class="help-dropdown-popper" @command="emit('user-command', $event)">
      <button class="user-card" type="button" :aria-label="`用户菜单：${username}`" :title="collapsed ? username : undefined">
        <span class="user-avatar">{{ userInitial }}</span>
        <span class="username">{{ username }}</span>
      </button>

      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item @click.prevent.stop>
            <div class="switch-account-inner" @mouseenter="openIdentitySubmenu" @mouseleave="closeIdentitySubmenu">
              <span class="switch-account-label">
                <span class="help-menu-item">
                  <svg class="help-menu-icon" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.3537 11.3537L11.3537 13.3537C11.2598 13.4475 11.1326 13.5003 10.9999 13.5003C10.8672 13.5003 10.74 13.4475 10.6462 13.3537C10.5523 13.2599 10.4996 13.1327 10.4996 13C10.4996 12.8673 10.5523 12.74 10.6462 12.6462L11.793 11.5H2.99991C2.8673 11.5 2.74012 11.4473 2.64635 11.3535C2.55258 11.2598 2.49991 11.1326 2.49991 11C2.49991 10.8674 2.55258 10.7402 2.64635 10.6464C2.74012 10.5527 2.8673 10.5 2.99991 10.5H11.793L10.6462 9.35372C10.5523 9.2599 10.4996 9.13265 10.4996 8.99997C10.4996 8.86729 10.5523 8.74004 10.6462 8.64622C10.74 8.5524 10.8672 8.49969 10.9999 8.49969C11.1326 8.49969 11.2598 8.5524 11.3537 8.64622L13.3537 10.6462C13.4001 10.6927 13.437 10.7478 13.4622 10.8085C13.4873 10.8692 13.5003 10.9343 13.5003 11C13.5003 11.0657 13.4873 11.1307 13.4622 11.1914C13.437 11.2521 13.4001 11.3073 13.3537 11.3537ZM4.64615 7.35372C4.73998 7.44754 4.86722 7.50025 4.99991 7.50025C5.13259 7.50025 5.25984 7.44754 5.35366 7.35372C5.44748 7.2599 5.50018 7.13265 5.50018 6.99997C5.50018 6.86729 5.44748 6.74004 5.35366 6.64622L4.20678 5.49997H12.9999C13.1325 5.49997 13.2597 5.44729 13.3535 5.35353C13.4472 5.25976 13.4999 5.13258 13.4999 4.99997C13.4999 4.86736 13.4472 4.74019 13.3535 4.64642C13.2597 4.55265 13.1325 4.49997 12.9999 4.49997H4.20678L5.35366 3.35372C5.44748 3.2599 5.50018 3.13265 5.50018 2.99997C5.50018 2.86729 5.44748 2.74004 5.35365 2.64622C5.25983 2.5524 5.13259 2.49969 4.99991 2.49969C4.86722 2.49969 4.73998 2.5524 4.64615 2.64622L2.64616 4.64622C2.59967 4.69266 2.56279 4.7478 2.53763 4.8085C2.51246 4.8692 2.49951 4.93427 2.49951 4.99997C2.49951 5.06568 2.51246 5.13074 2.53763 5.19144C2.56279 5.25214 2.59967 5.30729 2.64616 5.35372L4.64615 7.35372Z" fill="currentColor"/></svg>
                  账号切换
                </span>
                <svg class="arrow-icon" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </span>
            </div>
          </el-dropdown-item>
          <el-dropdown-item divided command="profile">
            <span class="help-menu-item">
              <svg class="help-menu-icon" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1.5C6.71442 1.5 5.45772 1.88122 4.3888 2.59545C3.31988 3.30968 2.48676 4.32484 1.99479 5.51256C1.50282 6.70028 1.37409 8.00721 1.6249 9.26809C1.8757 10.529 2.49477 11.6872 3.40381 12.5962C4.31285 13.5052 5.47104 14.1243 6.73192 14.3751C7.99279 14.6259 9.29973 14.4972 10.4874 14.0052C11.6752 13.5132 12.6903 12.6801 13.4046 11.6112C14.1188 10.5423 14.5 9.28558 14.5 8C14.4982 6.27665 13.8128 4.62441 12.5942 3.40582C11.3756 2.18722 9.72335 1.50182 8 1.5ZM4.63 12.3438C4.99171 11.7781 5.49001 11.3125 6.07895 10.99C6.6679 10.6676 7.32855 10.4985 8 10.4985C8.67146 10.4985 9.3321 10.6676 9.92105 10.99C10.51 11.3125 11.0083 11.7781 11.37 12.3438C10.4065 13.0931 9.22064 13.5 8 13.5C6.77936 13.5 5.59354 13.0931 4.63 12.3438ZM6 7.5C6 7.10444 6.1173 6.71776 6.33706 6.38886C6.55683 6.05996 6.86918 5.80362 7.23464 5.65224C7.60009 5.50087 8.00222 5.46126 8.39018 5.53843C8.77815 5.6156 9.13451 5.80608 9.41422 6.08579C9.69392 6.36549 9.8844 6.72186 9.96157 7.10982C10.0387 7.49778 9.99914 7.89991 9.84776 8.26537C9.69639 8.63082 9.44004 8.94318 9.11114 9.16294C8.78224 9.3827 8.39557 9.5 8 9.5C7.46957 9.5 6.96086 9.28929 6.58579 8.91421C6.21072 8.53914 6 8.03043 6 7.5ZM12.11 11.6506C11.5524 10.8425 10.7682 10.2174 9.85625 9.85375C10.3461 9.4679 10.7036 8.93899 10.879 8.34056C11.0543 7.74213 11.0388 7.10393 10.8346 6.51472C10.6305 5.9255 10.2477 5.41455 9.73971 5.05293C9.23168 4.69131 8.62359 4.49699 8 4.49699C7.37641 4.49699 6.76832 4.69131 6.26029 5.05293C5.75226 5.41455 5.36955 5.9255 5.16537 6.51472C4.96119 7.10393 4.9457 7.74213 5.12105 8.34056C5.2964 8.93899 5.65387 9.4679 6.14375 9.85375C5.23176 10.2174 4.44765 10.8425 3.89 11.6506C3.18536 10.8582 2.7248 9.87903 2.56378 8.83094C2.40276 7.78285 2.54816 6.71056 2.98245 5.74319C3.41675 4.77582 4.12142 3.95461 5.01163 3.37846C5.90184 2.80231 6.93962 2.49578 8 2.49578C9.06039 2.49578 10.0982 2.80231 10.9884 3.37846C11.8786 3.95461 12.5833 4.77582 13.0176 5.74319C13.4518 6.71056 13.5972 7.78285 13.4362 8.83094C13.2752 9.87903 12.8146 10.8582 12.11 11.6506Z" fill="currentColor"/></svg>
              个人信息
            </span>
          </el-dropdown-item>
          <el-dropdown-item divided command="logout">
            <span class="help-menu-item">
              <svg class="help-menu-icon" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M7.5 13.5C7.5 13.6326 7.44732 13.7598 7.35355 13.8536C7.25979 13.9473 7.13261 14 7 14H3C2.86739 14 2.74021 13.9473 2.64645 13.8536C2.55268 13.7598 2.5 13.6326 2.5 13.5V2.5C2.5 2.36739 2.55268 2.24021 2.64645 2.14645C2.74021 2.05268 2.86739 2 3 2H7C7.13261 2 7.25979 2.05268 7.35355 2.14645C7.44732 2.24021 7.5 2.36739 7.5 2.5C7.5 2.63261 7.44732 2.75979 7.35355 2.85355C7.25979 2.94732 7.13261 3 7 3H3.5V13H7C7.13261 13 7.25979 13.0527 7.35355 13.1464C7.44732 13.2402 7.5 13.3674 7.5 13.5ZM14.3538 7.64625L11.8538 5.14625C11.7599 5.05243 11.6327 4.99972 11.5 4.99972C11.3673 4.99972 11.2401 5.05243 11.1462 5.14625C11.0524 5.24007 10.9997 5.36732 10.9997 5.5C10.9997 5.63268 11.0524 5.75993 11.1462 5.85375L12.7931 7.5H7C6.86739 7.5 6.74021 7.55268 6.64645 7.64645C6.55268 7.74021 6.5 7.86739 6.5 8C6.5 8.13261 6.55268 8.25979 6.64645 8.35355C6.74021 8.44732 6.86739 8.5 7 8.5H12.7931L11.1462 10.1462C11.0524 10.2401 10.9997 10.3673 10.9997 10.5C10.9997 10.6327 11.0524 10.7599 11.1462 10.8538C11.2401 10.9476 11.3673 11.0003 11.5 11.0003C11.6327 11.0003 11.7599 10.9476 11.8538 10.8538L14.3538 8.35375C14.4002 8.30731 14.4371 8.25217 14.4623 8.19147C14.4874 8.13077 14.5004 8.06571 14.5004 8C14.5004 7.93429 14.4874 7.86923 14.4623 7.80853C14.4371 7.74783 14.4002 7.69269 14.3538 7.64625Z" fill="currentColor"/></svg>
              退出登录
            </span>
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>

    <teleport to="body">
      <div v-show="showIdentitySubmenu" class="identity-submenu" :style="submenuStyle"
        @mouseenter="cancelCloseIdentitySubmenu" @mouseleave="closeIdentitySubmenu">
        <div v-for="idt in identities" :key="idt.id" class="identity-option"
          :class="{ active: idt.id === currentIdentityId }" @click.stop="switchIdentity(idt)">
          <div class="identity-option-info">
            <span class="identity-option-name">{{ idt.identityUsername }}</span>
            <div class="identity-option-tags">
              <span class="identity-tag" :class="idt.identityType === 1 ? 'tag-personal' : 'tag-enterprise'">
                {{ idt.identityType === 1 ? '个人' : '企业' }}
              </span>
              <span v-if="idt.identityType === 2" class="identity-tag tag-role">
                {{ idt.role === 2 ? '管理员' : '成员' }}
              </span>
            </div>
          </div>
          <svg v-if="idt.id === currentIdentityId" class="check-icon" viewBox="0 0 16 16" fill="none">
            <path d="M3 8l3 3 7-7" stroke="#0840e6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
      </div>
    </teleport>

    <el-dialog v-model="showPromotionDialog" :show-close="false" width="420px" align-center class="promotion-dialog" style="padding:0;background:transparent;box-shadow:none;border-radius:0">
      <PromotionModal @close="showPromotionDialog = false" />
    </el-dialog>

    <ResearchPreferenceDialog v-model="showResearchPreferenceDialog" />

  </aside>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { loadResearchSharing, researchSharing } from '@/composables/researchSharing'
loadResearchSharing()
import { getBalance } from '@/api/billing'
import axios from 'axios'
import PromotionModal from '@/views/PromotionModal.vue'
import ResearchPreferenceDialog from '@/components/ResearchPreferenceDialog.vue'

const props = defineProps({
  user: { type: Object, default: null },
  identities: { type: Array, default: () => [] },
  activePath: { type: String, default: '/' },
  collapsed: { type: Boolean, default: false },
  isAdmin: { type: Boolean, default: false },
  isEnterpriseAdmin: { type: Boolean, default: false },
  isPersonalUser: { type: Boolean, default: false },
  messagesEnabled: { type: Boolean, default: false },
  messageUnreadCount: { type: Number, default: null },
  /** 研究偏好已保存过（或状态未查到）：不再蓝色高亮引导 */
  preferenceComplete: { type: Boolean, default: false },
})

const emit = defineEmits(['navigate', 'user-command', 'switch-identity', 'xbios-action', 'toggle-collapse', 'open-release-updates', 'open-messages', 'dialog-busy'])

const balance = ref(null)
const unreadGiftCount = ref(0)
let fetchRetryTimer = null
let balanceInflight = null
let lastFetchedAt = 0
// user 引用可能在短时间内被父组件重新赋值多次（如 storage 事件连环触发 checkLoginStatus），
// 这里做最小间隔节流，避免每次 watch 触发都各打一次 /api/billing/balance 撞 nginx 429。
const MIN_FETCH_INTERVAL_MS = 3000

// 获取余额，失败时保持上次有效值（不重置为 0）
const fetchBalance = (retry = true) => {
  if (!localStorage.getItem('token')) return
  if (balanceInflight) return balanceInflight
  if (retry && Date.now() - lastFetchedAt < MIN_FETCH_INTERVAL_MS) return

  balanceInflight = (async () => {
    try {
      const res = await getBalance()
      if (res.data?.code === 200) {
        balance.value = res.data.data?.balance ?? balance.value
      }
    } catch (err) {
      console.error('获取余额失败:', err)
      if (retry && !err.response?.status?.toString().startsWith('4')) {
        clearTimeout(fetchRetryTimer)
        fetchRetryTimer = setTimeout(() => fetchBalance(false), 3000)
      }
    } finally {
      lastFetchedAt = Date.now()
      balanceInflight = null
    }
  })()

  return balanceInflight
}

// 监听用户或身份变化，重新获取余额
watch(
  () => [props.user, props.user?.identityId],
  ([newUser]) => {
    clearTimeout(fetchRetryTimer)
    if (newUser) {
      fetchBalance()
    } else {
      balance.value = null
    }
  },
  { immediate: true }
)

async function refreshGiftReminders() {
  const identityId = props.user?.identityId
  if (!identityId || props.messagesEnabled) { unreadGiftCount.value = 0; return }
  try {
    const response = await axios.get('/api/subscriptions/gifts/unread-count')
    if (identityId === props.user?.identityId && !props.messagesEnabled) unreadGiftCount.value = Number(response.data?.data?.count || 0)
  } catch { if (identityId === props.user?.identityId) unreadGiftCount.value = 0 }
}
watch(() => [props.user?.identityId, props.activePath, props.messagesEnabled], refreshGiftReminders, { immediate: true })
window.addEventListener('billing-gift-reminders-updated', refreshGiftReminders)
onUnmounted(() => window.removeEventListener('billing-gift-reminders-updated', refreshGiftReminders))

const icons = {
  preference: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 5H3"></path><path d="M12 19H3"></path><path d="M14 3v4"></path><path d="M16 17v4"></path><path d="M21 12h-9"></path><path d="M21 19h-5"></path><path d="M21 5h-7"></path><path d="M8 10v4"></path><path d="M8 12H3"></path></svg>',
  project: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"></path><path d="M8 10v4"></path><path d="M12 10v2"></path><path d="M16 10v6"></path></svg>',
  data: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M3 5V19A9 3 0 0 0 21 19V5"></path><path d="M3 12A9 3 0 0 0 21 12"></path></svg>',
  community: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 7v10"></path><path d="M6 5v14"></path><rect width="12" height="18" x="10" y="3" rx="2"></rect></svg>',
  inspiration: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.341 6.484A10 10 0 0 1 10.266 21.85"></path><path d="M3.659 17.516A10 10 0 0 1 13.74 2.152"></path><circle cx="12" cy="12" r="3"></circle><circle cx="19" cy="5" r="2"></circle><circle cx="5" cy="19" r="2"></circle></svg>',
  building: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 12h4"></path><path d="M10 8h4"></path><path d="M14 21v-3a2 2 0 0 0-4 0v3"></path><path d="M6 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2"></path><path d="M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16"></path></svg>',
  shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="8" height="8" x="3" y="3" rx="2"></rect><path d="M7 11v4a2 2 0 0 0 2 2h4"></path><rect width="8" height="8" x="13" y="13" rx="2"></rect></svg>',
  coin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"></path><path d="M20 2v4"></path><path d="M22 4h-4"></path><circle cx="4" cy="20" r="2"></circle></svg>',
  help: '<svg viewBox="0 0 24 24" fill="none"><path d="M12 9.7998V19.9998M12 19.9998L11.4185 19.1277C10.9017 18.3526 10.6426 17.9638 10.2998 17.6821C9.99512 17.4317 9.64306 17.2441 9.26569 17.1295C8.83942 17 8.36994 17 7.43073 17H4.59797C4.03901 17 3.7596 17 3.5459 16.8911C3.35774 16.7952 3.20487 16.6419 3.10899 16.4537C3 16.2398 3 15.9601 3 15.4001V6.6001C3 6.04004 3 5.75981 3.10899 5.5459C3.20487 5.35774 3.35774 5.20487 3.5459 5.10899C3.75981 5 4.03956 5 4.59961 5H7.19961C8.87977 5 9.72004 5 10.3618 5.32698C10.9263 5.6146 11.3852 6.0737 11.6729 6.63818C11.9998 7.27992 12 8.11965 12 9.7998C12 8.11965 12 7.27992 12.327 6.63818C12.6146 6.0737 13.0732 5.6146 13.6377 5.32698C14.2794 5 15.1196 5 16.7998 5H19.3998C19.9599 5 20.2401 5 20.454 5.10899C20.6422 5.20487 20.7948 5.35774 20.8906 5.5459C20.9996 5.75981 21 6.04004 21 6.6001V15.4001C21 15.9601 20.9996 16.2398 20.8906 16.4537C20.7948 16.6419 20.6425 16.7952 20.4543 16.8911C20.2406 17 19.961 17 19.402 17H16.5693C15.6301 17 15.1597 17 14.7334 17.1295C14.356 17.2441 14.0057 17.4317 13.701 17.6821C13.3568 17.965 13.096 18.3557 12.575 19.1372L12 19.9998Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  promotion: '<svg viewBox="0 0 24 24" fill="none"><path d="M17 8h2a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M3 8h14v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M7 8V5a2 2 0 0 1 4 0v3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
}

const researchEntry = { path: '/ai-chat', label: '开始研究', icon: icons.project }
const nav = computed(() => [
  { path: '/dataset', label: '数聚空间', icon: icons.data },
  researchSharing.enabled ? { path: '/community', label: '成果社区', icon: icons.community } : null,
  // props.isPersonalUser ? { path: '/promotion', label: '推广赚币', icon: icons.promotion } : null,
  props.isEnterpriseAdmin ? { path: '/enterprise', label: '企业管理', icon: icons.building } : null,
].filter(Boolean))

const workbenchItems = computed(() => [
  props.isAdmin ? { path: '/admin', label: '管理后台', icon: icons.shield } : null,
].filter(Boolean))

const helpCollapsed = ref(false)

const helpItems = computed(() => [
  { command: 'docs', label: '使用指南', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 7v14"></path><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"></path></svg>' },
  { command: 'feedback', label: '问题反馈', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path><path d="M12 15h.01"></path><path d="M12 7v4"></path></svg>' },
  { command: props.messagesEnabled ? 'messages' : 'updates', label: props.messagesEnabled ? '消息通知' : '更新动态', icon: props.messagesEnabled ? '<svg data-icon="bell" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.268 21a2 2 0 0 0 3.464 0" /><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" /></svg>' : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><path d="M12 7v5l4 2"></path></svg>' },
])

const hasToken = computed(() => !!localStorage.getItem('token'))
const username = computed(() => props.user?.identityUsername || props.user?.username || '')
const userInitial = computed(() => username.value.charAt(0).toUpperCase() || '?')
const currentIdentityId = computed(() => props.user?.identityId)
const showIdentitySubmenu = ref(false)
const submenuStyle = ref({ left: '0px', top: '0px' })
let hide = null

const formatBalance = (val) => {
  if (val === null || val === undefined) return '--'
  const num = Math.floor(val)
  if (num >= 10000) {
    return (num / 10000).toFixed(1).replace(/\.0$/, '') + '万'
  }
  return num
}

const currentPath = computed(() => props.activePath.split(/[?#]/)[0])
const isActive = (path) => path === '/dataset'
  ? currentPath.value.startsWith('/dataset')
  : path === '/community'
  ? currentPath.value.startsWith('/community') || currentPath.value.startsWith('/my-shares')
  : currentPath.value === path || currentPath.value.startsWith(`${path}/`)
const isCapabilityActive = computed(() => (
  currentPath.value.startsWith('/capabilities') || currentPath.value.startsWith('/inspiration')
))

const openCapabilitySpace = () => {
  if (props.collapsed) {
    emit('toggle-collapse')
  }
  emit('navigate', '/capabilities')
}

const handleHelpCommand = (command) => {
  if (command === 'messages') { emit('open-messages'); return }
  if (command === 'docs') {
    window.open(
      'https://ycnz43zopd30.feishu.cn/wiki/QYDlw5CmSiiluYkpGhCcw0nsn1g?fromScene=spaceOverview',
      '_blank',
      'noopener,noreferrer',
    )
    return
  }

  if (command === 'feedback') {
    // 站内问题反馈页（原跳转飞书表单，表单链接保留在 /feedback 页面底部）
    emit('navigate', '/feedback')
    return
  }

  if (command === 'updates') {
    emit('open-release-updates')
    return
  }
}

const openIdentitySubmenu = (e) => {
  clearTimeout(hide)
  const rect = e.currentTarget.getBoundingClientRect()
  submenuStyle.value = {
    left: `${rect.right + 8}px`,
    top: `${rect.top}px`,
  }
  showIdentitySubmenu.value = true
}

const closeIdentitySubmenu = () => {
  hide = setTimeout(() => {
    showIdentitySubmenu.value = false
  }, 150)
}

const cancelCloseIdentitySubmenu = () => {
  clearTimeout(hide)
}

const switchIdentity = (identity) => {
  showIdentitySubmenu.value = false
  emit('switch-identity', identity)
}

// 推广页面
const showPromotionDialog = ref(false)
const showResearchPreferenceDialog = ref(false)
watch([showPromotionDialog, showResearchPreferenceDialog], ([promotionOpen, preferenceOpen]) => {
  emit('dialog-busy', promotionOpen || preferenceOpen)
})
onUnmounted(() => emit('dialog-busy', false))
const openPromotionDialog = () => {
  showPromotionDialog.value = true
}
</script>

<style scoped>
.app-sidebar {
  width: 51.2px;
  height: 100vh;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 0.8px solid #e5e7eb;
  background: #f5f7f8;
  color: #374151;
}

.brand {
  height: 51.2px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.brand-logo {
  display: block;
  flex-shrink: 0;
  width: 29px;
  height: 29px;
  border-radius: 10px;
  object-fit: cover;
  box-shadow: 0 7px 16px rgba(80, 74, 155, 0.2);
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6.4px;
  padding: 8px 4.8px;
}

.section-label {
  padding: 3.2px 0 0;
  color: #9ca3af;
  font-size: 8.8px;
  font-weight: 700;
  letter-spacing: 0;
}

.nav-item {
  width: 44.8px;
  min-height: 44.8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3.2px;
  padding: 4px 1.6px;
  border: 0.8px solid transparent;
  border-radius: 6.4px;
  background: transparent;
  color: #4b5563;
  font-size: 8.8px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: color 0.2s, background 0.2s, border-color 0.2s;
}

.nav-item:hover {
  color: #0840e6;
  background: rgba(8, 64, 230, 0.06);
}

.nav-item.active {
  color: #0840e6;
  background: rgba(8, 64, 230, 0.09);
  border-color: rgba(8, 64, 230, 0.2);
}

.nav-item.compact {
  min-height: 40px;
  font-size: 8.8px;
  font-weight: 500;
}

.help-item {
  align-self: center;
  margin-bottom: 1.6px;
}

.coin-item {
  align-self: center;
  margin-bottom: 1.6px;
}

.coin-icon {
  color: #7c3aed;
}

.coin-icon :deep(svg) {
  width: 16px;
  height: 16px;
}

.balance-amount {
  font-size: 8.8px;
  font-weight: 700;
  color: #7c3aed;
  margin-top: 1.6px;
}

.help-dropdown {
  align-self: center;
}

.nav-icon {
  width: 20.8px;
  height: 20.8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-icon :deep(svg) {
  width: 20.8px;
  height: 20.8px;
}

.nav-label {
  max-width: 43.2px;
  overflow: hidden;
  font-size: 8.8px;
  line-height: 1.2;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-spacer {
  flex: 1;
}

.free-claim-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 43px;
  height: 16px;
  border-radius: 6px;
  background: linear-gradient(160deg, #0840e6 45%, #7c3aed);
  color: #ffffff;
  font-size: 10px;
  line-height: 16px;
  font-weight: 500;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.free-claim-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4.8px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: transform 0.2s;
}

.free-claim-btn:hover {
  transform: scale(1.08);
}

.user-card {
  width: 44.8px;
  min-height: 44.8px;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  padding: 4px 1.6px;
  border: 0.8px solid transparent;
  border-radius: 6.4px;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.user-card:hover {
  background: rgba(8, 64, 230, 0.06);
}

.user-avatar {
  width: 28.8px;
  height: 28.8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6.4px !important;
  background: linear-gradient(135deg, #4A7EF0 0%, #0840E6 100%);
  color: #ffffff;
  font-size: 11.2px;
  font-weight: 700;
}

.chevron-icon,
.arrow-icon,
.check-icon {
  width: 11.2px;
  height: 11.2px;
  flex-shrink: 0;
}

.chevron-icon {
  display: none;
}

.switch-account-inner {
  display: flex;
  align-items: center;
  width: 100%;
}

.switch-account-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 6.4px;
}

.identity-submenu {
  position: fixed;
  min-width: 160px;
  z-index: 9999;
  padding: 4.8px;
  border: 0.8px solid #e5e7eb;
  border-radius: 6.4px;
  background: #ffffff;
  box-shadow: 0 3.2px 12.8px rgba(0, 0, 0, 0.08);
}

.identity-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6.4px;
  padding: 6.4px 8px;
  border-radius: 4.8px;
  cursor: pointer;
}

.identity-option:hover,
.identity-option.active {
  background: rgba(8, 64, 230, 0.08);
}

.identity-option-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3.2px;
}

.identity-option-name {
  color: #374151;
  font-size: 11.2px;
  font-weight: 600;
}

.identity-option-tags {
  display: flex;
  gap: 3.2px;
  flex-wrap: wrap;
}

.identity-tag {
  padding: 1.6px 4.8px;
  border-radius: 799.2px;
  font-size: 8.8px;
  line-height: 1.2;
}

.tag-personal {
  color: #2563eb;
  background: #dbeafe;
}

.tag-enterprise {
  color: #0840e6;
  background: #dbeafe;
}

.tag-role {
  color: #7c3aed;
  background: #ede9fe;
}

.help-menu-item {
  display: inline-flex;
  align-items: center;
  gap: 6.4px;
}

.help-menu-icon {
  width: 12.8px;
  height: 12.8px;
  flex-shrink: 0;
}

/* 展开态：参考工作台侧栏，图标在左、文字在右。 */
.app-sidebar {
  width: 201.6px;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  background: #ffffff;
  color: #13213c;
  transition: width 0.22s ease;
  scrollbar-width: none;
}

.app-sidebar::-webkit-scrollbar {
  display: none;
}

.brand {
  height: 67.2px;
  min-height: 67.2px;
  justify-content: flex-start;
  gap: 9.6px;
  padding: 0 36.8px 0 17.6px;
  white-space: nowrap;
}

.brand-logo {
  width: 29px;
  height: 29px;
  border-radius: 10px;
  box-shadow: 0 7px 16px rgba(80, 74, 155, 0.2);
}

.brand-name {
  color: #111b35;
  font-size: 17.6px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.brand-version {
  align-self: flex-start;
  margin-top: 19.2px;
  margin-left: -4.8px;
  color: #9aa5b8;
  font-size: 10.4px;
  font-weight: 500;
  white-space: nowrap;
}

/* 折叠按钮按原型 .bu-navigation-handle：栏缘外侧 32×48 的点击区（只占栏外半边，不压住侧栏内容），
   里面画直径 32 的半圆（16×32 探出栏缘）+ 10px 箭头；top 180 与 xBIOS 项目栏把手（原型 top 260）错开，两个把手不再挤在一起。 */
.sidebar-toggle {
  position: fixed;
  top: 180px;
  left: 201.6px;
  transform: translateY(-50%);
  z-index: 20;
  width: 32px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #69778f;
  cursor: pointer;
  transition: left 0.22s ease;
}

.sidebar-toggle-face {
  width: 16px;
  height: 32px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 2px;
  border: 1px solid #e3e9f4;
  border-left: 0;
  border-radius: 0 16px 16px 0;
  background: #ffffff;
  box-shadow: 2px 2px 7px rgba(0, 32, 96, 0.07);
  transition: background-color 0.16s ease, color 0.16s ease;
}

.sidebar-toggle:hover .sidebar-toggle-face {
  color: #0840e6;
  background: #f8faff;
}

.sidebar-toggle:focus-visible {
  outline: none;
}

.sidebar-toggle:focus-visible .sidebar-toggle-face {
  box-shadow: 0 0 0 2px #0840e6, 2px 2px 7px rgba(0, 32, 96, 0.07);
}

.sidebar-toggle svg {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
}

.research-preference-entry {
  min-height: 41px;
  margin: 6.4px 12.8px 11.2px;
  padding: 0 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 11px;
  flex-shrink: 0;
  color: #ffffff;
  background: #1244e8;
  border: 0;
  border-radius: 14.4px;
  text-align: left;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease, background .2s ease;
}

.research-preference-entry:hover {
  transform: translateY(-0.8px);
  background: #0b3ddc;
  box-shadow: 0 9.6px 19.2px rgba(8, 64, 230, .18);
}

/* 原型 .bu-new.is-preference-complete：透明底、正文色、字重 400，悬停浅底。 */
.research-preference-entry.is-preference-complete,
.app-sidebar.collapsed .research-preference-entry.is-preference-complete {
  color: #17233d;
  background: transparent;
}

.research-preference-entry.is-preference-complete:hover,
.app-sidebar.collapsed .research-preference-entry.is-preference-complete:hover {
  transform: none;
  color: #17233d;
  background: #f8faff;
  box-shadow: none;
}

.research-preference-entry.is-preference-complete .preference-entry-label {
  font-weight: 400;
}

.preference-entry-icon,
.preference-entry-icon :deep(svg) {
  width: 16px;
  height: 16px;
}

.preference-entry-icon { display: inline-flex; }
.preference-entry-label { font-size: 14px; line-height: 21px; font-weight: 500; white-space: nowrap; }

.sidebar-section-label {
  padding: 0 17.6px 5.6px;
  color: #8190a8;
  font-size: 12px;
  line-height: 21px;
  white-space: nowrap;
}

.sidebar-nav,
.sidebar-tools {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4.8px;
  padding: 6.4px 12.8px;
}

.capability-group {
  width: 100%;
}

.capability-trigger {
  font-weight: 430;
}

.capability-chevron {
  width: 12.8px;
  height: 12.8px;
  flex-shrink: 0;
  margin-left: auto;
  color: #71809a;
  transition: transform 0.2s ease;
}

.capability-chevron.open {
  transform: rotate(180deg);
}

.sidebar-tools {
  padding-top: 1.6px;
  padding-bottom: 3.2px;
}

.help-collapse-bar {
  display: flex;
  align-items: center;
  gap: 6.4px;
  margin: 3.2px 12.8px 6.4px;
  padding: 3.2px 0;
  border: 0;
  border-radius: 6.4px;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s;
}

.help-collapse-bar:hover {
  background: #f2f6ff;
}

.help-collapse-line {
  flex: 1;
  height: 0.8px;
  background: #e4e9f2;
  transition: background 0.2s;
}

.help-collapse-bar:hover .help-collapse-line {
  background: #cfd9ee;
}

.help-collapse-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #68758c;
  transition: transform 0.2s ease, color 0.2s;
}

.help-collapse-chevron svg {
  width: 9.6px;
  height: 9.6px;
}

.help-collapse-bar:hover .help-collapse-chevron {
  color: #0840e6;
}

/* 折叠箭头全站统一（2026-10-20 用户确认）：展开 ⌄，收起 ›。svg 本身是朝上的 ⌃。 */
.help-collapse-chevron {
  transform: rotate(180deg);
}

.help-collapse-bar.collapsed .help-collapse-chevron {
  transform: rotate(90deg);
}

.nav-item {
  width: 100%;
  min-height: 39px;
  flex-direction: row;
  justify-content: flex-start;
  gap: 11px;
  padding: 0 10px;
  border-radius: 8px;
  color: #17233f;
  font-size: 14px;
  font-weight: 430;
  text-align: left;
}

.nav-item:hover {
  color: #0840e6;
  background: #f2f6ff;
}

.nav-item.active {
  color: #0840e6;
  background: #eaf0ff;
  border-color: transparent;
  font-weight: 600;
}

.nav-icon {
  width: 16px;
  height: 16px;
}

.nav-icon :deep(svg) {
  width: 16px;
  height: 16px;
}

.nav-label {
  max-width: none;
  flex: 1;
  overflow: hidden;
  font-size: 14px;
  line-height: 21px;
  text-align: left;
  text-overflow: ellipsis;
}

.free-claim-btn {
  min-height: 24px;
}

.coin-item {
  width: auto;
  min-height: 40px;
  align-self: stretch;
  margin: 6.4px 12.8px 3.2px;
  padding: 0 11.2px;
  border-color: #dfe6f2;
  border-radius: 10.4px;
  background: #ffffff;
}

.coin-item:hover,
.coin-item.active {
  border-color: #cfd9ee;
  background: #f8faff;
}

.coin-icon {
  width: 19.2px;
  height: 19.2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.coin-icon :deep(svg) {
  width: 16px;
  height: 16px;
}

.coin-item .nav-label {
  color: #5e6b82;
}

.coin-reminder {
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background: #e74a57;
  color: #fff;
  font-size: 10px;
  line-height: 18px;
  text-align: center;
}

.balance-amount {
  margin: 0 0 0 auto;
  color: #d18a1b;
  font-size: 14px;
  font-weight: 500;
}

.help-dropdown {
  width: auto;
  align-self: stretch;
  margin: 6.4px 12.8px 14.4px;
}

.user-card {
  width: 100%;
  min-height: 44.8px;
  justify-content: flex-start;
  gap: 9.6px;
  padding: 5.6px 9.6px;
}

.user-avatar {
  width: 33.6px;
  height: 33.6px;
  flex-shrink: 0;
  border-radius: 50% !important;
  box-shadow: 0 6.4px 14.4px rgba(8, 64, 230, 0.2);
}

.username {
  min-width: 0;
  overflow: hidden;
  color: #0840e6;
  font-size: 14px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 收起态：按原型一级导航收起态（栏宽 72、内边距 10、按钮 51×36 圆角 9、间距 6、图标 16）。 */
.app-sidebar.collapsed {
  box-sizing: border-box;
  width: 72px;
  padding-bottom: 18px;
  border-right: 1px solid #e3e9f4;
}

.app-sidebar.collapsed .sidebar-toggle {
  left: 72px;
}

.app-sidebar.collapsed .brand {
  height: 50px;
  min-height: 50px;
  margin-top: 18px;
  justify-content: center;
  padding: 4px 0 17px;
}

.app-sidebar.collapsed .brand-logo {
  width: 29px;
  height: 29px;
  border-radius: 10px;
  box-shadow: 0 7px 16px rgba(80, 74, 155, 0.2);
}

.app-sidebar.collapsed .brand-name,
.app-sidebar.collapsed .brand-version,
.app-sidebar.collapsed .nav-label,
.app-sidebar.collapsed .balance-amount,
.app-sidebar.collapsed .username,
.app-sidebar.collapsed .preference-entry-label,
.app-sidebar.collapsed .sidebar-section-label {
  display: none;
}

.app-sidebar.collapsed .research-preference-entry {
  width: auto;
  min-height: 38px;
  margin: 0 10px 7px;
  padding: 0 11px;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: #0840e6;
}

.app-sidebar.collapsed .sidebar-nav,
.app-sidebar.collapsed .sidebar-tools {
  align-items: stretch;
  gap: 6px;
  padding: 0 10px;
}

.app-sidebar.collapsed .sidebar-nav + .sidebar-nav {
  margin-top: 6px;
}

.app-sidebar.collapsed .capability-group {
  width: 100%;
}

.app-sidebar.collapsed .capability-chevron {
  display: none;
}

.app-sidebar.collapsed .nav-item {
  width: 100%;
  min-height: 36px;
  justify-content: center;
  gap: 0;
  padding: 0 10px;
  border-radius: 9px;
}

.app-sidebar.collapsed .help-collapse-bar {
  margin-right: 10px;
  margin-left: 10px;
}

.app-sidebar.collapsed .coin-item {
  width: auto;
  min-height: 38px;
  align-self: stretch;
  margin: 8px 10px 8px;
  padding: 0 10px;
  border: 1px solid #e3e9f4;
  border-radius: 9px;
}

.app-sidebar.collapsed .help-dropdown {
  width: auto;
  align-self: stretch;
  margin: 0 10px;
}

.app-sidebar.collapsed .user-card {
  width: 100%;
  min-height: 43px;
  justify-content: center;
  padding: 7px 0;
}

.app-sidebar.collapsed .user-avatar {
  width: 29px;
  height: 29px;
}
</style>

<style>
.help-dropdown-popper {
  border-radius: 9.6px !important;
  overflow: hidden;
}

.help-dropdown-popper .el-dropdown-menu {
  border-radius: 9.6px !important;
  padding: 4.8px !important;
  min-width: 128px !important;
  background: #F5F7F8 !important;
}

.help-dropdown-popper .el-dropdown-menu__item {
  border-radius: 6.4px !important;
  background: transparent !important;
}

.help-dropdown-popper .el-dropdown-menu__item--divided {
  margin-top: 4.8px !important;
  border-color: #e5e7eb !important;
}

.promotion-dialog .el-dialog__header {
  display: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

.promotion-dialog .el-dialog__body {
  padding: 0 !important;
  margin: 0 !important;
}

.promotion-dialog .el-dialog__footer {
  display: none !important;
  padding: 0 !important;
}

.promotion-dialog .el-dialog {
  --el-dialog-padding-primary: 0;
  padding: 0 !important;
  background: transparent !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  overflow: visible;
}
</style>
